import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Phone, Menu, X, MessageSquare, BadgeCheck } from 'lucide-react';
const navLinks = [{
  name: 'Services',
  href: '#services'
}, {
  name: 'Gallery',
  href: '#gallery'
}, {
  name: 'Reviews',
  href: '#reviews'
}, {
  name: 'Areas',
  href: '#areas'
}, {
  name: 'Why Us',
  href: '#why-us'
}, {
  name: 'FAQ',
  href: '#faq'
}];
export function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState<string>('');
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Highlight the section currently in view
  useEffect(() => {
    const ids = navLinks.map(l => l.href.slice(1));
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) setActiveSection(entry.target.id);
      });
    }, {
      rootMargin: '-45% 0px -50% 0px'
    });
    ids.forEach(id => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);
  return <header className="fixed top-0 left-0 right-0 z-50" data-id="element-394" data-mp="3n3dwgukp9p3v">
      {/* Top utility strip */}
      <div className={`hidden lg:block bg-[#0E0E0E] text-gray-300 transition-all duration-300 overflow-hidden ${scrolled ? 'h-0 opacity-0' : 'h-9 opacity-100'}`} data-id="element-395" data-mp="3n1j8t8i1niw3">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-9 flex items-center justify-between text-xs" data-id="element-396" data-mp="uba2p1mqa91">
          <div className="flex items-center gap-2" data-id="element-397" data-mp="34cw4wtblszk3">
            <BadgeCheck size={14} className="text-[#C8102E]" data-id="element-398" data-mp="34c34ea38n6ae" />
            <span className="font-semibold tracking-wide" data-id="element-399" data-mp="39bfsey5uijek">
              Free estimates · Same-day &amp; next-day availability
            </span>
          </div>
          <span className="tracking-[0.2em] uppercase text-gray-500 font-semibold" data-id="element-400" data-mp="3196ryxzvfggw">
            Monterey, CA + 50 Miles
          </span>
        </div>
      </div>

      {/* Main bar */}
      <nav className={`transition-all duration-300 border-b ${scrolled ? 'bg-white/90 backdrop-blur-xl border-gray-200 shadow-[0_4px_24px_rgba(0,0,0,0.06)]' : 'bg-white border-transparent'}`} data-id="element-401" data-mp="3b0b8w393xbed">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-402" data-mp="2ddubyz4aczt5">
          <div className={`flex justify-between items-center transition-all duration-300 ${scrolled ? 'h-16' : 'h-20'}`} data-id="element-403" data-mp="2dfoyuiz4b1zv">
            {/* Logo */}
            <a href="#" className="flex items-center gap-3 flex-shrink-0 group" data-id="element-404" data-mp="2ndv151ou6a59">
              <img src="https://cdn.magicpatterns.com/uploads/hJguF2FSF4VF7Gq2iQCUa2/Jose_Junk_Removal_Logo_(1).png" alt="Jose's Junk Removal" className={`w-auto transition-all duration-300 ${scrolled ? 'h-11' : 'h-14'}`} data-id="element-405" data-mp="7ijbuag4619b" />
              <span className="hidden sm:flex flex-col leading-none" data-id="element-406" data-mp="f1iv8g3krsso">
                <span className="font-oswald text-lg xl:text-xl font-bold tracking-wide text-[#1A1A1A]" data-id="element-407" data-mp="15x70b561d1b">
                  JOSE'S JUNK REMOVAL
                </span>
                <span className="text-[10px] tracking-[0.22em] uppercase text-gray-500 font-bold mt-1" data-id="element-408" data-mp="sv0c1t4vmrf0">
                  Haul · Demo · Cleanout
                </span>
              </span>
            </a>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center gap-1" data-id="element-409" data-mp="k2q1ubtzelxr">
              {navLinks.map(link => {
              const isActive = activeSection === link.href.slice(1);
              return <a key={link.name} href={link.href} className={`relative px-3 py-2 font-semibold text-sm uppercase tracking-wider transition-colors ${isActive ? 'text-[#C8102E]' : 'text-gray-600 hover:text-[#1A1A1A]'}`} data-id="element-410" data-mp="1bsupqkgy9r3q">
                    {link.name}
                    {isActive && <motion.span layoutId="nav-active" className="absolute left-3 right-3 -bottom-0.5 h-[2px] bg-[#C8102E] rounded-full" data-id="element-411" data-mp="3b37exmrtomk8" />}
                  </a>;
            })}

              <a href="tel:8312407401" className="ml-4 flex items-center gap-2 bg-[#C8102E] text-white px-5 py-3 rounded-lg font-bold hover:bg-[#8B0A20] transition-colors shadow-[0_6px_20px_rgba(200,16,46,0.3)]" data-id="element-412" data-mp="1wfuzojyeg4v">
                <Phone size={17} data-id="element-413" data-mp="ufd3qkwivq8h" />
                <span className="whitespace-nowrap" data-id="element-414" data-mp="pma9sbnu2err">(831) 240-7401</span>
              </a>
            </div>

            {/* Mobile actions */}
            <div className="lg:hidden flex items-center gap-2" data-id="element-415" data-mp="28beeupvnn9qa">
              <a href="tel:8312407401" aria-label="Call Jose's Junk Removal" className="flex items-center justify-center w-11 h-11 rounded-lg bg-[#C8102E] text-white shadow-md" data-id="element-416" data-mp="2d7v5bqqbu06j">
                <Phone size={20} data-id="element-417" data-mp="2m78cgcqzaco3" />
              </a>
              <button onClick={() => setIsOpen(!isOpen)} aria-label={isOpen ? 'Close menu' : 'Open menu'} aria-expanded={isOpen} className="flex items-center justify-center w-11 h-11 rounded-lg border border-gray-200 text-gray-800 hover:bg-gray-50 transition-colors" data-id="element-418" data-mp="2r97g7nqmrblk">
                {isOpen ? <X size={22} data-id="element-419" data-mp="351nbu3h7c7cg" /> : <Menu size={22} data-id="element-420" data-mp="39z5oa3ufh4p7" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Nav */}
        <AnimatePresence data-id="element-421" data-mp="3ez1u88mor67e">
          {isOpen && <motion.div initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} transition={{
          duration: 0.25,
          ease: [0.23, 1, 0.32, 1]
        }} className="lg:hidden overflow-hidden bg-white border-t border-gray-100 shadow-xl" data-id="element-422" data-mp="3evnnypawicuo">
              <div className="px-4 py-4" data-id="element-423" data-mp="1ju26xsvr918l">
                <div className="grid grid-cols-2 gap-2 mb-4" data-id="element-424" data-mp="1xm8kr8bfqw4v">
                  {navLinks.map(link => <a key={link.name} href={link.href} onClick={() => setIsOpen(false)} className="px-4 py-4 rounded-lg bg-[#F9F9F9] border border-gray-100 text-sm font-bold text-gray-800 hover:border-[#C8102E] hover:text-[#C8102E] transition-colors uppercase tracking-wide text-center" data-id="element-425" data-mp="1tkx43sajhl8t">
                      {link.name}
                    </a>)}
                </div>

                <div className="flex gap-2" data-id="element-426" data-mp="2junuwtyfgt3x">
                  <a href="tel:8312407401" className="flex-1 flex items-center justify-center gap-2 bg-[#C8102E] text-white px-4 py-4 rounded-lg font-bold shadow-md" data-id="element-427" data-mp="2jvyu0ggxhcop">
                    <Phone size={18} data-id="element-428" data-mp="2a2rqz29nu8rs" />
                    <span data-id="element-429" data-mp="2f3fg1iebwfjd">Call</span>
                  </a>
                  <a href="sms:8312407401" className="flex-1 flex items-center justify-center gap-2 bg-[#1A1A1A] text-white px-4 py-4 rounded-lg font-bold shadow-md" data-id="element-430" data-mp="2f1un1x7nx4nm">
                    <MessageSquare size={18} data-id="element-431" data-mp="1r68dy1vqk0py" />
                    <span data-id="element-432" data-mp="1r79z4z6gu3le">Text</span>
                  </a>
                </div>

                <p className="text-center text-xs text-gray-500 font-semibold mt-4 uppercase tracking-widest" data-id="element-433" data-mp="1w64j44g65y73">
                  Free Estimates · Monterey + 50 Miles
                </p>
              </div>
            </motion.div>}
        </AnimatePresence>
      </nav>
    </header>;
}